/* eslint-disable */

import { l10n } from 'i18n/i18nUtil';
import {
  DEFAULT_CONFIGURATION_NAME_REGEX,
  DisallowedDefaultConfigurationUsernames,
  PRODUCT_NAME_CONSTANTS,
  VRA_CIDR_IP_REGEX,
  WINDOWS_DOMAIN_NAME_REGEX
} from 'vrlcm-gui-constants';
import { EnvironmentRequestProduct } from 'vrlcm-gui-types';
import { safeJsonParse, gte } from 'vrlcm-gui-utils';
import * as Yup from 'yup';
import {
  FIELD_MAPPING,
  PRODUCT_MAPPING,
  VRA_K8_CIDR_PATTER_ERROR_MESSAGE,
  VSSC_STANDALONE_TENANT
} from '../constants/AppConstants';
import { DisplayMetadata } from '../types/Common';
import { UIEnvironmentState } from '../types/Environments';
import { major } from '../utils/UtilFunctions';
import { vrniDSNodesDisplayMetadata } from './vrni-ds-nodes-display-md';

export const eula = l10n('lcops.createEnvironment.eula');

export const productDisplayMetadata = (
  selectedProducts: string[],
  isImport: boolean,
  isInstallFlow: boolean,
  isVcfEnvironment: boolean,
  isAddProduct: boolean,
  isVidmInstalled: boolean,
  existingProducts: EnvironmentRequestProduct[],
  environmentsWithVrops: UIEnvironmentState[], // vrops is installed in any of the environments. This is required for vIDM
  selectedVsscTenantId?: string,
  vsscVersionSupportingIntegration?: boolean,
  authenticationType?: string
) => {
  const allvRATenants = existingProducts
    .filter(p => p.id === 'vra')
    .map(p => (safeJsonParse(p.properties.__vraTenantList) as string[]) || [])
    .reduce((acc, cur) => acc.concat(cur), []);

  const deployedVsscTenants = existingProducts
    .filter(p => p.id === 'vssc')
    .map(p => p.properties.tenantId);

  const isVra = existingProducts.filter(p => p.id === 'vra');

  const isVraInstalled = isVra.length > 0;

  const isVsscVraSelected =
    selectedVsscTenantId && selectedVsscTenantId !== VSSC_STANDALONE_TENANT;

  const isVSphereAuthenticationType = authenticationType === 'vsphere';

  const vrbAuthTypeOptions = [
    {
      textLabel: 'Standalone',
      value: 'standalone'
    },
    {
      textLabel: PRODUCT_NAME_CONSTANTS.vidm,
      value: 'vidm',
      disabled: !isVidmInstalled
    }
  ];
  const products = selectedProducts.reduce<{ [key: string]: string }>(
    (acc, cur) => {
      const [id, version] = cur.split(':');
      return {
        ...acc,
        [id]: version
      };
    },
    {}
  );
  if ((products.vra && major(products.vra) < 8) || isImport) {
    vrbAuthTypeOptions.push({
      textLabel: l10n('lcops.createEnvironment.mocks.vra'),
      value: 'vra'
    });
  }
  let vrliExists = false;
  let vropsExists = false;
  let vropsForceIpv6Exists = false;

  if (isInstallFlow) {
    vrliExists = !!products.vrli;
    vropsExists = !!products.vrops;
    vropsForceIpv6Exists = !!products.vrops && gte(products.vrops, '8.13.1');
  } else if (isAddProduct) {
    vrliExists = existingProducts.some(p => p.id === 'vrli') || !!products.vrli;
    vropsExists =
      existingProducts.some(p => p.id === 'vrops') || !!products.vrops;
    vropsForceIpv6Exists =
      existingProducts.some(
        p => p.id === 'vrops' && gte(p.version, '8.13.1')
      ) ||
      (!!products.vrops && gte(products.vrops, '8.13.1'));
  }

  const isVropsVIDBCompatVersion =
    !isVcfEnvironment && products.vrops && gte(products.vrops, '8.18.0');
  const isVrliVIDBCompatVersion =
    !isVcfEnvironment && products.vrli && gte(products.vrli, '8.18.0');

  return {
    vra: [
      {
        name: 'installInitialContent',
        validationRules: [],
        displayRules: [],
        order: 0
      },
      {
        name: 'nodeSize',
        validationRules: [
          {
            name: 'required',
            message: l10n('lcops.createEnvironment.mocks.required')
          }
        ],
        data: {
          options: [
            {
              textLabel: l10n('lcops.createEnvironment.mocks.medium'),
              value: 'medium'
            },
            {
              textLabel: l10n('lcops.createEnvironment.mocks.xLarge'),
              value: 'xlarge'
            }
          ]
        },
        displayRules: [],
        order: 0
      },
      {
        name: 'fipsMode',
        validationRules: [],
        displayRules: [],
        data: {
          options: [
            {
              textLabel: l10n('lcops.createEnvironment.mocks.on'),
              value: 'true'
            },
            {
              textLabel: l10n('lcops.createEnvironment.mocks.off'),
              value: 'false'
            }
          ]
        },
        order: 1
      },
      {
        name: 'monitorvRAWithvROps',
        validationRules: [],
        displayRules:
          products.vra && major(products.vra) === 7
            ? [
                {
                  fieldRef: 'installInitialContent',
                  fieldRules: [
                    {
                      name: 'isChecked',
                      value: true
                    }
                  ]
                }
              ]
            : [],
        order: 2
      },
      {
        name: 'registervROpswithvRA',
        validationRules: [],
        displayRules: [],
        order: 3
      },
      {
        name: 'useComponentCertificate',
        validationRules: [],
        displayRules: [],
        order: 4
      },
      {
        name: 'certificate',
        validationRules: [
          {
            name: 'required',
            message: l10n('lcops.createEnvironment.mocks.required')
          }
        ],
        displayRules:
          products.vra && products.vra.startsWith('7')
            ? [
                {
                  fieldRef: 'useComponentCertificate',
                  fieldRules: [
                    {
                      name: 'notChecked',
                      value: false
                    }
                  ]
                }
              ]
            : [],
        order: 5
      },
      {
        name: 'cafeCertificate',
        validationRules: [
          {
            name: 'required',
            message: l10n('lcops.createEnvironment.mocks.required')
          }
        ],
        displayRules: [
          {
            fieldRef: 'useComponentCertificate',
            fieldRules: [
              {
                name: 'isChecked',
                value: true
              }
            ]
          }
        ],
        order: 6
      },
      {
        name: 'msCertificate',
        validationRules: [
          {
            name: 'required',
            message: l10n('lcops.createEnvironment.mocks.required')
          }
        ],
        displayRules: [
          {
            fieldRef: 'useComponentCertificate',
            fieldRules: [
              {
                name: 'isChecked',
                value: true
              }
            ]
          }
        ],
        order: 7
      },
      {
        name: 'webCertificate',
        validationRules: [
          {
            name: 'required',
            message: l10n('lcops.createEnvironment.mocks.required')
          }
        ],
        displayRules: [
          {
            fieldRef: 'useComponentCertificate',
            fieldRules: [
              {
                name: 'isChecked',
                value: true
              }
            ]
          }
        ],
        order: 8
      },
      {
        name: 'vraImportVersion',
        validationRules: [],
        data: {
          options: [
            {
              textLabel: '8.x',
              value: '8.0.0',
              disabled: !isVidmInstalled
            },
            {
              textLabel: '7.x',
              value: '7.6.0'
            }
          ]
        },
        displayRules: [],
        order: 8
      },
      {
        name: 'windowsUserName',
        validationRules: [
          {
            name: 'required',
            message: l10n(
              'lcops.createEnvironment.mocks.windowsUsernameRequired'
            )
          },
          {
            name: 'pattern',
            message: l10n('lcops.createEnvironment.mocks.usernameFormat'),
            value: WINDOWS_DOMAIN_NAME_REGEX
          }
        ],
        displayRules: isImport
          ? [
              {
                fieldRef: 'vraImportVersion',
                fieldRules: [
                  {
                    name: 'is7.x',
                    value: '7.6.0'
                  }
                ]
              }
            ]
          : [],
        order: 9
      },
      {
        name: 'windowsPassword',
        validationRules: [
          {
            name: 'required',
            message: l10n(
              'lcops.createEnvironment.mocks.windowsPasswordRequired'
            )
          },
          {
            name: 'pattern',
            message: l10n('lcops.createEnvironment.mocks.passwordPattern'),
            value: /^[^£"]+$/
          }
        ],
        displayRules: isImport
          ? [
              {
                fieldRef: 'vraImportVersion',
                fieldRules: [
                  {
                    name: 'is7.x',
                    value: '7.6.0'
                  }
                ]
              }
            ]
          : [],
        order: 10
      },
      {
        name: 'configureClusterVIP',
        validationRules: [],
        data: {
          options: [
            {
              textLabel: l10n('lcops.createEnvironment.mocks.yes'),
              value: 'true'
            },
            {
              textLabel: l10n('lcops.createEnvironment.mocks.no'),
              value: 'false'
            }
          ]
        },
        displayRules: [],
        order: 11
      },
      {
        name: 'configureWindowVM',
        validationRules: [],
        data: {
          options: [
            {
              textLabel: l10n('lcops.createEnvironment.mocks.yes'),
              value: 'true'
            },
            {
              textLabel: l10n('lcops.createEnvironment.mocks.no'),
              value: 'false'
            }
          ]
        },
        displayRules: [],
        order: 12
      },
      {
        name: 'affinityRule',
        validationRules: [],
        displayRules: [],
        order: 13
      },
      {
        name: 'configureAffinitySeparateAll',
        validationRules: [],
        data: {
          options: [
            {
              textLabel: l10n(
                'lcops.createEnvironment.mocks.configureAffinitySeparateAll'
              ),
              value: 'true'
            },
            {
              textLabel: l10n(
                'lcops.createEnvironment.mocks.configureAffinitySameHost'
              ),
              value: 'false'
            }
          ]
        },
        displayRules: [
          {
            fieldRef: 'affinityRule',
            fieldRules: [
              {
                name: 'isChecked',
                value: true
              }
            ]
          }
        ],
        order: 14
      },
      {
        name: 'cafeHostNamePrimary',
        validationRules: [
          {
            name: 'required',
            message: l10n('lcops.createEnvironment.mocks.required')
          },
          {
            name: 'hostname',
            message: l10n('lcops.createEnvironment.mocks.hostname')
          }
        ],
        displayRules: [],
        order: 15
      },
      {
        name: 'cafeRootPasswordPrimary',
        validationRules: [
          {
            name: 'required',
            message: l10n('lcops.createEnvironment.mocks.required')
          }
        ],
        displayRules: [],
        order: 16
      },
      {
        name: 'tenantName',
        validationRules: [
          {
            name: 'required',
            message: l10n('lcops.createEnvironment.mocks.required')
          }
        ],
        displayRules: isImport
          ? [
              {
                fieldRef: 'vraImportVersion',
                fieldRules: [
                  {
                    name: 'is7.x',
                    value: '7.6.0'
                  }
                ]
              }
            ]
          : [],
        order: 17
      },
      {
        name: 'tenantUserName',
        validationRules: [
          {
            name: 'required',
            message: l10n('lcops.createEnvironment.mocks.required')
          }
        ],
        displayRules: isImport
          ? [
              {
                fieldRef: 'vraImportVersion',
                fieldRules: [
                  {
                    name: 'is7.x',
                    value: '7.6.0'
                  }
                ]
              }
            ]
          : [],
        order: 18
      },
      {
        name: 'tenantUserPassword',
        validationRules: [
          {
            name: 'required',
            message: l10n('lcops.createEnvironment.mocks.required')
          }
        ],
        displayRules: isImport
          ? [
              {
                fieldRef: 'vraImportVersion',
                fieldRules: [
                  {
                    name: 'is7.x',
                    value: '7.6.0'
                  }
                ]
              }
            ]
          : [],
        order: 19
      },
      {
        name: 'vraAdminPassword',
        validationRules: [
          {
            name: 'required',
            message: l10n('lcops.createEnvironment.mocks.required')
          }
        ],
        displayRules: isImport
          ? [
              {
                fieldRef: 'vraImportVersion',
                fieldRules: [
                  {
                    name: 'is7.x',
                    value: '7.6.0'
                  }
                ]
              }
            ]
          : [],
        order: 20
      },
      {
        name: 'productPassword',
        validationRules: [
          {
            name: 'required',
            message: l10n('lcops.createEnvironment.mocks.required')
          }
        ],
        displayRules: [],
        order: 21
      },
      {
        name: 'vraK8ClusterCidr',
        validationRules: [
          {
            name: 'patternExcludeEmptyString',
            value: VRA_CIDR_IP_REGEX,
            message: VRA_K8_CIDR_PATTER_ERROR_MESSAGE
          }
        ],
        order: 23
      },
      {
        name: 'vraK8ServiceCidr',
        validationRules: [
          {
            name: 'required',
            message: l10n('lcops.createEnvironment.mocks.required')
          },
          {
            name: 'patternExcludeEmptyString',
            value: VRA_CIDR_IP_REGEX,
            message: VRA_K8_CIDR_PATTER_ERROR_MESSAGE
          },
          {
            name: 'notOneOf',
            value: [Yup.ref('vraK8ClusterCidr')],
            message: `${FIELD_MAPPING.vraK8ServiceCidr} should not be same as ${FIELD_MAPPING.vraK8ClusterCidr}`
          }
        ],
        displayRules: [
          {
            fieldRef: 'vraK8ClusterCidr',
            fieldRules: [
              {
                name: 'exists',
                type: 'EXISTENCE'
              }
            ]
          }
        ],
        order: 24
      },
      {
        name: 'vidmDirectoryConfigRequestsList',
        validationRules: [
          {
            name: 'adList'
          }
        ],
        displayRules: [],
        validationFieldType: 'object',
        order: 25
      },
      {
        name: 'masterVidmEnabled',
        validationRules: [],
        displayRules: [],
        order: 99
      },
      {
        name: 'vCenterList',
        validationRules: [
          {
            name: 'anyChecked',
            message: l10n('lcops.createEnvironment.mocks.vCenterListChecked')
          }
        ],
        displayRules: [],
        validationFieldType: 'array',
        order: 100
      },
      {
        name: 'integrateWithSddcManager',
        validationRules: [],
        displayRules: [],
        order: 100
      },
      {
        name: 'sddcManagerHostname',
        displayRules: [],
        validationRules: [
          {
            name: 'required',
            message: l10n(
              'lcops.createEnvironment.mocks.sddcManagerHostnameRequired'
            )
          },
          {
            name: 'hostname',
            message: l10n('lcops.createEnvironment.mocks.hostname')
          }
        ],
        order: 100
      },
      {
        name: 'sddcManagerUsername',
        validationRules: [
          {
            name: 'required',
            message: l10n(
              'lcops.createEnvironment.mocks.sddcManagerUsernameRequired'
            )
          }
        ],
        displayRules: [],
        order: 100
      },
      {
        name: 'sddcManagerPassword',
        validationRules: [
          {
            name: 'required',
            message: l10n(
              'lcops.createEnvironment.mocks.sddcManagerPasswordRequired'
            )
          }
        ],
        displayRules: [],
        order: 100
      }
    ]
      .concat(
        !isImport && vrliExists
          ? [
              {
                name: 'vrliLogForwardingConfiguration',
                validationRules: [],
                displayRules: [],
                order: 100
              }
            ]
          : []
      )
      .concat(
        !isImport && vropsExists
          ? [
              {
                name: 'monitorWithvROps',
                validationRules: [],
                displayRules: [],
                order: 100
              }
            ]
          : []
      ),
    vrops: [
      {
        name: 'disableTls',
        validationRules: [],
        data: {
          options: [
            {
              textLabel: l10n('lcops.createEnvironment.mocks.TLSv1'),
              value: 'TLSv1'
            },
            {
              textLabel: l10n('lcops.createEnvironment.mocks.TLSv1.1'),
              value: 'TLSv1.1'
            },
            {
              textLabel: l10n('lcops.createEnvironment.mocks.TLSv1_TLSv1.1'),
              value: 'TLSv1,TLSv1.1'
            }
          ]
        },
        displayRules: [],
        order: 0
      },
      {
        name: 'fipsMode',
        validationRules: [],
        displayRules: [],
        data: {
          options: [
            {
              textLabel: l10n('lcops.createEnvironment.mocks.on'),
              value: 'true'
            },
            {
              textLabel: l10n('lcops.createEnvironment.mocks.off'),
              value: 'false'
            }
          ]
        },
        order: 1
      },
      {
        name: 'certificate',
        validationRules: [
          {
            name: 'required',
            message: l10n('lcops.createEnvironment.mocks.required')
          }
        ],
        displayRules: [],
        order: 1
      },
      {
        name: 'affinityRule',
        validationRules: [],
        displayRules: [],
        order: 2
      },
      {
        name: 'configureAffinitySeparateAll',
        validationRules: [],
        data: {
          options: [
            {
              textLabel: l10n(
                'lcops.createEnvironment.mocks.configureAffinitySeparateAll'
              ),
              value: 'true'
            },
            {
              textLabel: l10n(
                'lcops.createEnvironment.mocks.configureAffinitySameHost'
              ),
              value: 'false'
            }
          ]
        },
        displayRules: [
          {
            fieldRef: 'affinityRule',
            fieldRules: [
              {
                name: 'isChecked',
                value: true
              }
            ]
          }
        ],
        order: 3
      },
      {
        name: 'vropsMasterNodeIP',
        validationRules: [
          {
            name: 'required',
            message: l10n('lcops.createEnvironment.mocks.required')
          },
          {
            name: 'ipAddressOrFqdn',
            message: l10n('lcops.createEnvironment.mocks.required')
          }
        ],
        displayRules: [],
        order: 4
      },
      {
        name: 'vropsAdminPassword',
        validationRules: [
          {
            name: 'required',
            message: l10n('lcops.createEnvironment.mocks.required')
          }
        ],
        displayRules: [],
        order: 5
      },
      {
        name: 'rootPassword',
        validationRules: [
          {
            name: 'required',
            message: l10n('lcops.createEnvironment.mocks.required')
          }
        ],
        displayRules: [],
        order: 6
      },
      {
        name: 'productPassword',
        validationRules: [
          {
            name: 'required',
            message: l10n('lcops.createEnvironment.mocks.required')
          }
        ],
        displayRules: [],
        order: 7
      },
      {
        name: 'clusterFqdn',
        validationRules: [
          {
            name: 'required',
            message: l10n('lcops.createEnvironment.mocks.required')
          },
          {
            name: 'hostname',
            message: l10n('lcops.createEnvironment.mocks.hostname')
          }
        ],
        displayRules: [],
        order: 8
      },
      {
        name: 'vCenterList',
        validationRules: [
          {
            name: 'anyChecked',
            message: l10n('lcops.createEnvironment.mocks.vCenterListChecked')
          }
        ],
        displayRules: [],
        validationFieldType: 'array',
        order: 100
      },
      {
        name: 'installSddcManagementPack',
        validationRules: [],
        displayRules: [],
        order: 100
      }
    ]
      .concat(
        !isVropsVIDBCompatVersion
          ? [
              {
                name: 'masterVidmEnabled',
                validationRules: [],
                displayRules: [],
                order: 99
              }
            ]
          : []
      )
      .concat(
        vropsForceIpv6Exists
          ? [
              {
                name: 'forceIpV6',
                validationRules: [],
                displayRules: [],
                order: 100
              }
            ]
          : []
      )
      .concat(
        !isImport && vrliExists
          ? [
              {
                name: 'vrliLogForwardingConfiguration',
                validationRules: [],
                displayRules: [],
                order: 100
              }
            ]
          : []
      )
      .concat(
        !isImport && vrliExists
          ? [
              {
                name: 'vropsLogEntities',
                validationRules: [],
                displayRules: [],
                order: 100
              }
            ]
          : []
      ),
    vrli: [
      {
        name: 'nodeSize',
        validationRules: [
          {
            name: 'required',
            message: l10n('lcops.createEnvironment.mocks.required')
          }
        ],
        data: {
          options: [
            {
              textLabel: l10n('lcops.createEnvironment.mocks.small'),
              value: 'small'
            },
            {
              textLabel: l10n('lcops.createEnvironment.mocks.medium'),
              value: 'medium'
            },
            {
              textLabel: l10n('lcops.createEnvironment.mocks.large'),
              value: 'large'
            }
          ]
        },
        displayRules: [],
        order: 0
      },
      {
        name: 'fipsMode',
        validationRules: [],
        displayRules: [],
        data: {
          options: [
            {
              textLabel: l10n('lcops.createEnvironment.mocks.on'),
              value: 'true'
            },
            {
              textLabel: l10n('lcops.createEnvironment.mocks.off'),
              value: 'false'
            }
          ]
        },
        order: 1
      },
      {
        name: 'certificate',
        validationRules: [
          {
            name: 'required',
            message: l10n('lcops.createEnvironment.mocks.required')
          }
        ],
        displayRules: [],
        order: 1
      },
      {
        name: 'affinityRule',
        validationRules: [],
        displayRules: [],
        order: 2
      },
      {
        name: 'configureAffinitySeparateAll',
        validationRules: [],
        data: {
          options: [
            {
              textLabel: l10n(
                'lcops.createEnvironment.mocks.configureAffinitySeparateAll'
              ),
              value: 'true'
            },
            {
              textLabel: l10n(
                'lcops.createEnvironment.mocks.configureAffinitySameHost'
              ),
              value: 'false'
            }
          ]
        },
        displayRules: [
          {
            fieldRef: 'affinityRule',
            fieldRules: [
              {
                name: 'isChecked',
                value: true
              }
            ]
          }
        ],
        order: 3
      },
      {
        name: 'isUpgradeVmCompatibility',
        validationRules: [],
        displayRules: [],
        order: 4
      },
      {
        name: 'vrliAlwaysUseEnglish',
        validationRules: [],
        displayRules: [],
        order: 5
      },
      {
        name: 'vrliHostName',
        validationRules: [
          {
            name: 'required',
            message: l10n('lcops.createEnvironment.mocks.required')
          },
          {
            name: 'hostname',
            message: l10n('lcops.createEnvironment.mocks.hostname')
          }
        ],
        displayRules: [],
        order: 6
      },
      {
        name: 'adminEmail',
        validationRules: [
          {
            name: 'required',
            message: l10n('lcops.createEnvironment.mocks.required')
          },
          {
            name: 'email',
            message: l10n('lcops.createEnvironment.mocks.email')
          }
        ],
        displayRules: [],
        order: 7
      },
      {
        name: 'vrliAdminPassword',
        validationRules: [
          {
            name: 'required',
            message: l10n('lcops.createEnvironment.mocks.required')
          }
        ],
        displayRules: [],
        order: 8
      },
      {
        name: 'rootPassword',
        validationRules: [
          {
            name: 'required',
            message: l10n('lcops.createEnvironment.mocks.required')
          }
        ],
        displayRules: [],
        order: 9
      },
      {
        name: 'productPassword',
        validationRules: [
          {
            name: 'required',
            message: l10n('lcops.createEnvironment.mocks.required')
          }
        ],
        displayRules: [],
        order: 10
      },
      {
        name: 'clusterFqdn',
        validationRules: [
          {
            name: 'required',
            message: l10n('lcops.createEnvironment.mocks.required')
          },
          {
            name: 'hostname',
            message: l10n('lcops.createEnvironment.mocks.hostname')
          }
        ],
        displayRules: [],
        order: 11
      },
      {
        name: 'vCenterList',
        validationRules: [
          {
            name: 'anyChecked',
            message: l10n('lcops.createEnvironment.mocks.vCenterListChecked')
          }
        ],
        displayRules: [],
        validationFieldType: 'array',
        order: 100
      }
    ]
      .concat(
        !isVrliVIDBCompatVersion
          ? [
              {
                name: 'masterVidmEnabled',
                validationRules: [],
                displayRules: [],
                order: 99
              }
            ]
          : []
      )
      .concat(
        !isVcfEnvironment
          ? [
              {
                name: 'configureClusterVIP',
                validationRules: [],
                data: {
                  options: [
                    {
                      textLabel: l10n('lcops.createEnvironment.mocks.yes'),
                      value: 'true'
                    },
                    {
                      textLabel: l10n('lcops.createEnvironment.mocks.no'),
                      value: 'false'
                    }
                  ]
                },
                displayRules: [],
                order: 2
              }
            ]
          : []
      )
      .concat(
        !isImport && vropsExists
          ? [
              {
                name: 'monitorWithvROps',
                validationRules: [],
                displayRules: [],
                order: 100
              }
            ]
          : []
      ),
    vrni: [
      {
        name: 'fipsMode',
        validationRules: [],
        displayRules: [],
        data: {
          options: [
            {
              textLabel: l10n('lcops.createEnvironment.mocks.on'),
              value: 'true'
            },
            {
              textLabel: l10n('lcops.createEnvironment.mocks.off'),
              value: 'false'
            }
          ]
        },
        order: 1
      },
      {
        name: 'certificate',
        validationRules: [
          {
            name: 'required',
            message: l10n('lcops.createEnvironment.mocks.required')
          }
        ],
        displayRules: [],
        order: 1
      },
      {
        name: 'affinityRule',
        validationRules: [],
        displayRules: [],
        order: 2
      },
      {
        name: 'configureAffinitySeparateAll',
        validationRules: [],
        data: {
          options: [
            {
              textLabel: l10n(
                'lcops.createEnvironment.mocks.configureAffinitySeparateAll'
              ),
              value: 'true'
            },
            {
              textLabel: l10n(
                'lcops.createEnvironment.mocks.configureAffinitySameHost'
              ),
              value: 'false'
            }
          ]
        },
        displayRules: [
          {
            fieldRef: 'affinityRule',
            fieldRules: [
              {
                name: 'isChecked',
                value: true
              }
            ]
          }
        ],
        order: 3
      },
      {
        name: 'platformIP',
        validationRules: [
          {
            name: 'required',
            message: l10n('lcops.createEnvironment.mocks.required')
          },
          {
            name: 'ipAddress',
            message: l10n('lcops.createEnvironment.mocks.required')
          }
        ],
        displayRules: [],
        order: 4
      },
      {
        name: 'vrniPlatformUserName',
        validationRules: [
          {
            name: 'required',
            message: l10n('lcops.createEnvironment.mocks.required')
          }
        ],
        displayRules: [],
        order: 5
      },
      {
        name: 'vrniPlatformPassword',
        validationRules: [
          {
            name: 'required',
            message: l10n('lcops.createEnvironment.mocks.required')
          }
        ],
        displayRules: [],
        order: 6
      },
      {
        name: 'supportPassword',
        validationRules: [
          {
            name: 'required',
            message: l10n('lcops.createEnvironment.mocks.required')
          }
        ],
        displayRules: [],
        order: 7
      },
      {
        name: 'consolePassword',
        validationRules: [
          {
            name: 'required',
            message: l10n('lcops.createEnvironment.mocks.required')
          }
        ],
        displayRules: [],
        order: 8
      },
      {
        name: 'productPassword',
        validationRules: [
          {
            name: 'required',
            message: l10n('lcops.createEnvironment.mocks.required')
          }
        ],
        displayRules: [],
        order: 9
      },
      {
        name: 'monitorvRNIWithvROps',
        validationRules: [],
        displayRules: [],
        order: 10
      },
      {
        name: 'masterVidmEnabled',
        validationRules: [],
        displayRules: [],
        order: 99
      },
      {
        name: 'vCenterList',
        validationRules: [
          {
            name: 'anyChecked',
            message: l10n('lcops.createEnvironment.mocks.vCenterListChecked')
          }
        ],
        displayRules: [],
        validationFieldType: 'array',
        order: 100
      }
    ].concat(
      !isImport && vropsExists
        ? [
            {
              name: 'monitorWithvROps',
              validationRules: [],
              displayRules: [],
              order: 100
            }
          ]
        : []
    ),
    vrbc: [
      {
        name: 'currency',
        validationRules: [
          {
            name: 'required',
            message: l10n('lcops.createEnvironment.mocks.currencyRequierd')
          }
        ],
        data: {
          options: [
            'AED - UAE Dirham',
            'ALL - Albanian Lek',
            'ARS - Argentine Peso',
            'AUD - Australian Dollar',
            'AWG - Arubian Florin',
            'BBD - Barbadian Dollar',
            'BDT - Bangladeshi Taka',
            'BGN - Bulgarian Lev',
            'BHD - Bahraini Dinar',
            'BIF - Burundi Franc',
            'BMD - Bermudian Dollar',
            'BND - Brunei Dollar',
            'BOB - Bolivian Boliviano',
            'BRL - Brazilian Real',
            'BSD - Bahamian Dollar',
            'BWP - Botswana Pula',
            'BZD - Belize Dollar',
            'CAD - Canadian Dollar',
            'CDF - Congolese Franc',
            'CHF - Swiss Franc',
            'CLP - Chilean Peso',
            'CNY - China Yuan Renminbi',
            'COP - Colombian Peso',
            'CRC - Costa Rican Colon',
            'CUP - Cuban Peso',
            'CVE - Cape Verdean Escuso',
            'CZK - Czech Koruna',
            'DJF - Djiboutian Franc',
            'DKK - Danish Krone',
            'DOP - Dominican peso',
            'DZD - Algerian Dinar',
            'EGP - Egyptian Pound',
            'ETB - Etiopian Birr',
            'EUR - Euro',
            'FJD - Fijian Dollar',
            'GBP - British Pound',
            'GHS - Ghanaian Cedi',
            'GMD - Gambian Dalasi',
            'GNF - Guinean Franc',
            'GTQ - Guatemalan Quetzal',
            'HKD - Hong Kong Dollar',
            'HNL - Honduran Lempira',
            'HRK - Croatian Kuna',
            'HTG - Haitian Gourde',
            'HUF - Hungarian Forint',
            'IDR - Indonesia Rupiah',
            'ILS - Israeli Shekel',
            'INR - Indian Rupee',
            'IQD - Iraqi Dinar',
            'ISK - Icelandic Krona',
            'JMD - Jamaican Dollar',
            'JOD - Jordanian Dinar',
            'JPY - Japanese Yen',
            'KES - Kenyan Shilling',
            'KHR - Cambodian Riel',
            'KMF - Comorial Franc',
            'KRW - Korea(South) Won',
            'KWD - Kuwait Dinar',
            'KYD - Cayman Island Dollar',
            'KZT - Kazakhstani Tenge',
            'LAK - Lao Kip',
            'LBP - Lebanese Pound',
            'LKR - Sri Lankan Rupee',
            'LRD - Liberian Dollar',
            'LSL - Lesotho Loti',
            'LTL - Lithuanian Litas',
            'LYD - Libyan Dinar',
            'MAD - Moroccan Dirham',
            'MDL - Moldovan Leu',
            'MGA - Malagasy Ariary',
            'MKD - Macedonian Denar',
            'MMK - Myanmar Kyat',
            'MOP - Macanese Pataca',
            'MRO - Mauritanian Ouguiya',
            'MUR - Mauritian Rupee',
            'MVR - Maldivian Rufiyaa',
            'MWK - Malawian Kwacha',
            'MXN - Mexican Peso',
            'MYR - Malaysia Ringgit',
            'MZN - Mozambican Metical',
            'NAD - Nambian Dollar',
            'NGN - Nigerian Naira',
            'NIO - Nicaraguan Cordoba',
            'NOK - Norway Krone',
            'NPR - Nepalese Rupee',
            'NZD - New Zealand Dollar',
            'OMR - Omani Rial',
            'PAB - Panamanian Balboa',
            'PEN - Peruvian Sol',
            'PGK - Papua New Guinean Kina',
            'PHP - Philippine Peso',
            'PKR - Pakistani Rupee',
            'PLN - Polish Zloty',
            'PYG - Paraguayan Guarani',
            'QAR - Qatari Riyal',
            'RON - Romanian Leu',
            'RSD - Serbian Dinar',
            'RUB - Russia Ruble',
            'RWF - Rwandan Franc',
            'SAR - Saudi Arabian Riyal',
            'SCR - Seychellios Rupee',
            'SDG - Sudanese Pound',
            'SEK - Sweden Krona',
            'SGD - Singapore Dollar',
            'SHP - Saint Helena Pound',
            'SLL - Sierra Leonean Leone',
            'SOS - Somali Shilling',
            'STD - Sao Tome and Principe Dobra',
            'SVC - Salvadoran Colon',
            'SZL - Swazi Lilangeni',
            'THB - Thai Baht',
            'TMT - Turkmen Manat',
            'TND - Tunisian Dinar',
            'TRY - Turkey Lira',
            'TTD - Trinidad and Tobago Dollar',
            'TWD - Taiwan New Dollar',
            'TZS - Tanzanian Shilling',
            'UAH - Ukrainian Hryvnia',
            'UGX - Ugandan Shilling',
            'USD - US Dollar',
            'UYU - Uruguayan Peso',
            'UZS - Uzbekistani Som',
            'VEF - Venezuelan Bolivar',
            'VND - Vietnamese Dong',
            'XAF - Central African Franc',
            'XCD - East Caribbean Dollar',
            'XOF - West African Franc',
            'XPF - CFP Franc',
            'YER - Yemeni Rial',
            'ZAR - South Africa Rand'
          ].map(i => ({
            textLabel: i,
            value: i
          }))
        },
        displayRules: [],
        order: 0
      },
      {
        name: 'certificate',
        validationRules: [
          {
            name: 'required',
            message: l10n('lcops.createEnvironment.mocks.required')
          }
        ],
        displayRules: [],
        order: 1
      },
      {
        name: 'affinityRule',
        validationRules: [],
        displayRules: [],
        order: 2
      },
      {
        name: 'configureAffinitySeparateAll',
        validationRules: [],
        data: {
          options: [
            {
              textLabel: l10n(
                'lcops.createEnvironment.mocks.configureAffinitySeparateAll'
              ),
              value: 'true'
            },
            {
              textLabel: l10n(
                'lcops.createEnvironment.mocks.configureAffinitySameHost'
              ),
              value: 'false'
            }
          ]
        },
        displayRules: [
          {
            fieldRef: 'affinityRule',
            fieldRules: [
              {
                name: 'isChecked',
                value: true
              }
            ]
          }
        ],
        order: 3
      },
      {
        name: 'vrbHostName',
        validationRules: [
          {
            name: 'required',
            message: l10n('lcops.createEnvironment.mocks.required')
          },
          {
            name: 'hostname',
            message: l10n('lcops.createEnvironment.mocks.hostname')
          }
        ],
        displayRules: [],
        order: 4
      },
      {
        name: 'vrbRootPassword',
        validationRules: [
          {
            name: 'required',
            message: l10n('lcops.createEnvironment.mocks.required')
          }
        ],
        displayRules: [],
        order: 5
      },
      {
        name: 'vrbAuthType',
        validationRules: [
          {
            name: 'required',
            message: l10n('lcops.createEnvironment.mocks.required')
          }
        ],
        data: {
          options: vrbAuthTypeOptions
        },
        displayRules: [],
        order: 6
      },
      {
        name: 'vrbCafeHost',
        validationRules: [
          {
            name: 'required',
            message: l10n('lcops.createEnvironment.mocks.required')
          },
          {
            name: 'hostname',
            message: l10n('lcops.createEnvironment.mocks.hostname')
          }
        ],
        displayRules: [
          {
            fieldRef: 'vrbAuthType',
            fieldRules: [
              {
                name: 'vra',
                value: 'vra'
              }
            ]
          }
        ],
        order: 7
      },
      {
        name: 'vrbCafeSsoTenant',
        validationRules: [
          {
            name: 'required',
            message: l10n('lcops.createEnvironment.mocks.required')
          }
        ],
        displayRules: [
          {
            fieldRef: 'vrbAuthType',
            fieldRules: [
              {
                name: 'vra',
                value: 'vra'
              }
            ]
          }
        ],
        order: 8
      },
      {
        name: 'vrbCafeSsoAdminUser',
        validationRules: [
          {
            name: 'required',
            message: l10n('lcops.createEnvironment.mocks.required')
          }
        ],
        displayRules: [
          {
            fieldRef: 'vrbAuthType',
            fieldRules: [
              {
                name: 'vra',
                value: 'vra'
              }
            ]
          }
        ],
        order: 9
      },
      {
        name: 'vrbCafeSsoPassword',
        validationRules: [
          {
            name: 'required',
            message: l10n('lcops.createEnvironment.mocks.required')
          }
        ],
        displayRules: [
          {
            fieldRef: 'vrbAuthType',
            fieldRules: [
              {
                name: 'vra',
                value: 'vra'
              }
            ]
          }
        ],
        order: 10
      },
      {
        name: 'vrbVidmHostName',
        validationRules: [
          {
            name: 'required',
            message: l10n('lcops.createEnvironment.mocks.required')
          },
          {
            name: 'hostname',
            message: l10n('lcops.createEnvironment.mocks.hostname')
          }
        ],
        displayRules: [
          {
            fieldRef: 'vrbAuthType',
            fieldRules: [
              {
                name: 'vidm',
                value: 'vidm'
              }
            ]
          }
        ],
        order: 11
      },
      {
        name: 'vrbVidmAdminUserName',
        validationRules: [
          {
            name: 'required',
            message: l10n('lcops.createEnvironment.mocks.required')
          }
        ],
        displayRules: [
          {
            fieldRef: 'vrbAuthType',
            fieldRules: [
              {
                name: 'vidm',
                value: 'vidm'
              }
            ]
          }
        ],
        order: 12
      },
      {
        name: 'vrbVidmAdminPassword',
        validationRules: [
          {
            name: 'required',
            message: l10n('lcops.createEnvironment.mocks.required')
          }
        ],
        displayRules: [
          {
            fieldRef: 'vrbAuthType',
            fieldRules: [
              {
                name: 'vidm',
                value: 'vidm'
              }
            ]
          }
        ],
        order: 13
      },
      {
        name: 'productPassword',
        validationRules: [
          {
            name: 'required',
            message: l10n('lcops.createEnvironment.mocks.required')
          }
        ],
        displayRules: [],
        order: 14
      },
      {
        name: 'masterVidmEnabled',
        validationRules: [],
        displayRules: [],
        order: 99
      },
      {
        name: 'vCenterList',
        validationRules: [
          {
            name: 'anyChecked',
            message: l10n('lcops.createEnvironment.mocks.vCenterListChecked')
          }
        ],
        displayRules: [],
        validationFieldType: 'array',
        order: 100
      }
    ],
    vidm: [
      {
        name: 'certificate',
        validationRules: [
          {
            name: 'required',
            message: l10n('lcops.createEnvironment.mocks.required')
          }
        ],
        displayRules: [],
        order: 0
      },
      {
        name: 'nodeSize',
        validationRules: [
          {
            name: 'required',
            message: l10n('lcops.createEnvironment.mocks.required')
          }
        ],
        data: {
          options: [
            {
              textLabel: l10n('lcops.createEnvironment.mocks.xSmall'),
              value: 'xsmall'
            },
            {
              textLabel: l10n('lcops.createEnvironment.mocks.small'),
              value: 'small'
            },
            {
              textLabel: l10n('lcops.createEnvironment.mocks.mediumVra', [
                PRODUCT_MAPPING.vra
              ]),
              value: 'medium'
            },
            {
              textLabel: l10n('lcops.createEnvironment.mocks.large'),
              value: 'large'
            },
            {
              textLabel: l10n('lcops.createEnvironment.mocks.xLarge'),
              value: 'xlarge'
            },
            {
              textLabel: l10n('lcops.createEnvironment.mocks.xxLarge'),
              value: 'xxlarge'
            }
          ]
        },
        displayRules: [],
        order: 1
      },
      {
        name: 'hostName',
        validationRules: [
          {
            name: 'required',
            message: l10n('lcops.createEnvironment.mocks.required')
          },
          {
            name: 'hostname',
            message: l10n('lcops.createEnvironment.mocks.hostname')
          }
        ],
        displayRules: [],
        order: 1
      },
      {
        name: 'fipsMode',
        validationRules: [],
        displayRules: [],
        data: {
          options: [
            {
              textLabel: l10n('lcops.createEnvironment.mocks.on'),
              value: 'true'
            },
            {
              textLabel: l10n('lcops.createEnvironment.mocks.off'),
              value: 'false'
            }
          ]
        },
        order: 1
      },
      {
        name: 'vidmAdName',
        validationRules: [
          {
            name: 'required',
            message: l10n('lcops.createEnvironment.mocks.required')
          }
        ],
        displayRules: [],
        order: 2
      },
      {
        name: 'uberAdmin',
        validationRules: [
          {
            name: 'required',
            message: l10n('lcops.createEnvironment.mocks.required')
          }
        ],
        displayRules: [],
        order: 3
      },
      {
        name: 'vidmAdBindPassword',
        validationRules: [
          {
            name: 'required',
            message: l10n('lcops.createEnvironment.mocks.required')
          }
        ],
        displayRules: [],
        order: 4
      },
      {
        name: 'vidmAdBaseDN',
        validationRules: [
          {
            name: 'required',
            message: l10n('lcops.createEnvironment.mocks.required')
          }
        ],
        displayRules: [],
        order: 5
      },
      {
        name: 'vidmAdBindDN',
        validationRules: [
          {
            name: 'required',
            message: l10n('lcops.createEnvironment.mocks.required')
          }
        ],
        displayRules: [],
        order: 6
      },
      {
        name: 'vidmAdGroupDN',
        validationRules: [
          {
            name: 'required',
            message: l10n('lcops.createEnvironment.mocks.required')
          }
        ],
        displayRules: [],
        order: 7
      },
      {
        name: 'vidmAdUserDN',
        validationRules: [
          {
            name: 'required',
            message: l10n('lcops.createEnvironment.mocks.required')
          }
        ],
        displayRules: [],
        order: 8
      },
      {
        name: 'vidmSystemAdminPassword',
        validationRules: [
          {
            name: 'required',
            message: l10n('lcops.createEnvironment.mocks.required')
          }
        ],
        displayRules: [],
        order: 9
      },
      {
        name: 'vidmAdminPassword',
        validationRules: [
          {
            name: 'required',
            message: l10n('lcops.createEnvironment.mocks.required')
          }
        ],
        displayRules: [],
        order: 10
      },
      {
        name: 'vidmSshPassword',
        validationRules: [
          {
            name: 'required',
            message: l10n('lcops.createEnvironment.mocks.required')
          }
        ],
        displayRules: [],
        order: 11
      },
      {
        name: 'vidmRootPassword',
        validationRules: [
          {
            name: 'required',
            message: l10n('lcops.createEnvironment.mocks.required')
          }
        ],
        displayRules: [],
        order: 12
      },
      {
        name: 'defaultConfigurationEmail',
        validationRules: [
          {
            name: 'required',
            message: l10n('lcops.createEnvironment.mocks.required')
          },
          {
            name: 'email',
            message: l10n('lcops.createEnvironment.mocks.email')
          }
        ],
        displayRules: [],
        order: 13
      },
      {
        name: 'defaultConfigurationUsername',
        validationRules: [
          {
            name: 'required',
            message: l10n('lcops.createEnvironment.mocks.required')
          },
          {
            name: 'pattern',
            value: DEFAULT_CONFIGURATION_NAME_REGEX,
            message: l10n(
              'lcops.createEnvironment.mocks.defaultConfigUsernamePattern',
              [DisallowedDefaultConfigurationUsernames.join(', ')]
            )
          }
        ],
        displayRules: [],
        order: 14
      },
      {
        name: 'defaultConfigurationPassword',
        validationRules: [
          {
            name: 'required',
            message: l10n('lcops.createEnvironment.mocks.required')
          }
        ],
        displayRules: [],
        order: 15
      },
      {
        name: 'syncGroupMembers',
        validationRules: [],
        displayRules: [],
        order: 16
      },
      {
        name: 'vCenterList',
        validationRules: [
          {
            name: 'anyChecked',
            message: l10n('lcops.createEnvironment.mocks.vCenterListChecked')
          }
        ],
        displayRules: [],
        validationFieldType: 'array',
        order: 100
      }
    ]
      .concat(
        !isImport && environmentsWithVrops.length > 0
          ? [
              {
                name: 'monitorWithvROps',
                validationRules: [],
                displayRules: [],
                order: 100
              }
            ]
          : []
      )
      .concat(
        !isImport && environmentsWithVrops.length > 0
          ? [
              {
                name: 'vROpsEnvironmentId',
                validationRules: [
                  {
                    name: 'required',
                    message: l10n('lcops.createEnvironment.mocks.required')
                  }
                ],
                data: {
                  options: environmentsWithVrops.map(
                    ({
                      environmentRequestDTO: { environmentId, environmentName }
                    }) => ({
                      textLabel: environmentName,
                      value: environmentId
                    })
                  )
                },
                displayRules: [],
                order: 100
              }
            ]
          : []
      ),
    vssc: [
      {
        name: 'vsscHostName',
        validationRules: [
          {
            name: 'required',
            message: l10n('lcops.createEnvironment.mocks.required')
          },
          {
            name: 'hostname',
            message: l10n('lcops.createEnvironment.mocks.hostname')
          }
        ],
        displayRules: [],
        order: 0
      },
      {
        name: 'vrasscAdminPassword',
        validationRules: [
          {
            name: 'required',
            message: l10n('lcops.createEnvironment.mocks.required')
          }
        ],
        displayRules: [],
        order: 1
      },
      {
        name: 'rootPassword',
        validationRules: [
          {
            name: 'required',
            message: l10n('lcops.createEnvironment.mocks.required')
          }
        ],
        displayRules: [],
        order: 2
      },
      {
        name: 'vCenterList',
        validationRules: [
          {
            name: 'anyChecked',
            message: l10n('lcops.createEnvironment.mocks.vCenterListChecked')
          }
        ],
        displayRules: [],
        validationFieldType: 'array',
        order: 100
      },
      {
        name: 'certificate',
        validationRules: [
          {
            name: 'required',
            message: l10n('lcops.createEnvironment.mocks.required')
          }
        ],
        displayRules: [],
        order: 1
      },
      {
        name: 'productPassword',
        validationRules: [
          {
            name: 'required',
            message: l10n('lcops.createEnvironment.mocks.required')
          }
        ],
        displayRules: [],
        order: 0
      },
      {
        name: 'fipsMode',
        validationRules: [],
        displayRules: [],
        data: {
          options: [
            {
              textLabel: l10n('lcops.createEnvironment.mocks.on'),
              value: 'true'
            },
            {
              textLabel: l10n('lcops.createEnvironment.mocks.off'),
              value: 'false'
            }
          ]
        },
        order: 1
      },
      isVsscVraSelected &&
        vsscVersionSupportingIntegration && {
          name: 'masterVidmEnabled',
          validationRules: [],
          displayRules: [],
          order: 99
        }
    ].concat(
      isAddProduct && isVraInstalled && isVsscVraSelected
        ? ([
            {
              name: 'tenantId',
              validationRules: [
                {
                  name: 'required',
                  message: l10n('lcops.createEnvironment.mocks.tenantRequired')
                }
              ],
              data: {
                options: allvRATenants

                  .filter(
                    vraTenant =>
                      !deployedVsscTenants.some(
                        deployedVsscTenant =>
                          deployedVsscTenant.toLowerCase() ===
                          vraTenant.toLowerCase()
                      )
                  )
                  .map(t => ({
                    textLabel: t,
                    value: t
                  }))
              },
              displayRules: [],
              order: 1
            }
          ] as any[])
        : []
    ),
    vro: [
      {
        name: 'fipsMode',
        validationRules: [],
        displayRules: [],
        data: {
          options: [
            {
              textLabel: l10n('lcops.createEnvironment.mocks.on'),
              value: 'true'
            },
            {
              textLabel: l10n('lcops.createEnvironment.mocks.off'),
              value: 'false'
            }
          ]
        },
        order: 1
      },
      {
        name: 'certificate',
        validationRules: [
          {
            name: 'required',
            message: l10n('lcops.createEnvironment.mocks.required')
          }
        ],
        displayRules: [],
        order: 1
      },
      {
        name: 'productPassword',
        validationRules: [
          {
            name: 'required',
            message: l10n('lcops.createEnvironment.mocks.required')
          }
        ],
        displayRules: [],
        order: 7
      },
      {
        name: 'vroHostName',
        validationRules: [
          {
            name: 'required',
            message: l10n('lcops.createEnvironment.mocks.required')
          },
          {
            name: 'hostname',
            message: l10n('lcops.createEnvironment.mocks.hostname')
          }
        ],
        displayRules: [],
        order: 6
      },
      {
        name: 'vCenterList',
        validationRules: [
          {
            name: 'anyChecked',
            message: l10n('lcops.createEnvironment.mocks.vCenterListChecked')
          }
        ],
        displayRules: [],
        validationFieldType: 'array',
        order: 100
      },
      {
        name: 'rootPassword',
        validationRules: [
          {
            name: 'required',
            message: l10n('lcops.createEnvironment.mocks.required')
          }
        ],
        displayRules: [],
        order: 7
      },
      {
        name: 'masterVidmEnabled',
        validationRules: [],
        displayRules: [],
        order: 99
      }
    ]
      .concat(
        !isInstallFlow && isVraInstalled && !isVSphereAuthenticationType
          ? ([
              {
                name: 'tenantId',
                validationRules: [
                  {
                    name: 'required',
                    message: l10n(
                      'lcops.createEnvironment.mocks.tenantRequired'
                    )
                  }
                ],
                displayRules: [],
                order: 1
              },
              {
                name: 'vidmTenantAdmin',
                validationRules: [
                  {
                    name: 'required',
                    message: l10n('lcops.createEnvironment.mocks.required')
                  }
                ],
                displayRules: [],
                order: 99
              },
              {
                name: 'vidmTenantAdminPassword',
                validationRules: [
                  {
                    name: 'required',
                    message: l10n('lcops.createEnvironment.mocks.required')
                  }
                ],
                displayRules: [],
                order: 99
              }
            ] as any[])
          : []
      )
      .concat(
        !isImport && vropsExists
          ? [
              {
                name: 'monitorWithvROps',
                validationRules: [],
                displayRules: [],
                order: 100
              }
            ]
          : []
      )
      .concat(
        !isImport && isVSphereAuthenticationType
          ? [
              {
                name: 'vCenterHostForAuthentication',
                validationRules: [
                  {
                    name: 'required',
                    message: l10n('lcops.createEnvironment.mocks.required')
                  }
                ],
                displayRules: [],
                order: 100
              }
            ]
          : []
      ),
    cloudproxy: [
      {
        name: 'proxyName',
        validationRules: [
          {
            name: 'required',
            message: l10n('lcops.createEnvironment.mocks.required')
          }
        ],
        displayRules: [],
        order: 0
      },
      {
        name: 'cloudProxyServiceTypeCloudAssembly',
        displayRules: [],
        validationRules: [],
        order: 1
      },
      {
        name: 'cloudProxyServiceTypeLogInsight',
        displayRules: [],
        validationRules: [],
        order: 2
      },
      {
        name: 'cloudProxyServiceTypevRCSM',
        displayRules: [],
        validationRules: [],
        order: 3
      },
      {
        name: 'productPassword',
        validationRules: [
          {
            name: 'required',
            message: l10n('lcops.createEnvironment.mocks.required')
          }
        ],
        displayRules: [],
        order: 4
      },
      {
        name: 'cloudProxyRefreshToken',
        validationRules: [
          {
            name: 'required',
            message: l10n('lcops.createEnvironment.mocks.required')
          }
        ],
        displayRules: [],
        order: 5
      },
      {
        name: 'hostName',
        validationRules: [
          {
            name: 'required',
            message: l10n('lcops.createEnvironment.mocks.required')
          },
          {
            name: 'hostname',
            message: l10n('lcops.createEnvironment.mocks.hostname')
          }
        ],
        displayRules: [],
        order: 6
      },
      {
        name: 'ip',
        validationRules: [
          {
            name: 'required',
            message: l10n('lcops.createEnvironment.mocks.required')
          },
          {
            name: 'ipAddress',
            message: l10n('lcops.createEnvironment.mocks.required')
          }
        ],
        displayRules: [],
        order: 7
      },
      {
        name: 'rootPassword',
        validationRules: [
          {
            name: 'required',
            message: l10n('lcops.createEnvironment.mocks.required')
          }
        ],
        displayRules: [],
        order: 8
      },
      {
        name: 'vCenterList',
        validationRules: [
          {
            name: 'anyChecked',
            message: l10n('lcops.createEnvironment.mocks.vCenterListChecked')
          }
        ],
        displayRules: [],
        validationFieldType: 'array',
        order: 9
      },
      {
        name: 'networkProxySettings',
        displayRules: [],
        validationRules: [],
        order: 100
      }
    ],
    vropscloudproxy: [
      {
        name: 'productPassword',
        validationRules: [
          {
            name: 'required',
            message: l10n('lcops.createEnvironment.mocks.required')
          }
        ],
        displayRules: [],
        order: 1
      },
      {
        name: 'proxyName',
        validationRules: [
          {
            name: 'required',
            message: l10n('lcops.createEnvironment.mocks.required')
          }
        ],
        displayRules: [],
        order: 0
      },
      {
        name: 'cloudProxyRefreshToken',
        validationRules: [
          {
            name: 'required',
            message: l10n('lcops.createEnvironment.mocks.required')
          }
        ],
        displayRules: [],
        order: 2
      }
    ],
    vrnicloudproxy: [
      {
        name: 'productPassword',
        validationRules: [
          {
            name: 'required',
            message: l10n('lcops.createEnvironment.mocks.required')
          }
        ],
        displayRules: [],
        order: 1
      },
      {
        name: 'proxyName',
        validationRules: [
          {
            name: 'required',
            message: l10n('lcops.createEnvironment.mocks.required')
          }
        ],
        displayRules: [],
        order: 0
      },
      {
        name: 'cloudProxyRefreshToken',
        validationRules: [
          {
            name: 'required',
            message: l10n('lcops.createEnvironment.mocks.required')
          }
        ],
        displayRules: [],
        order: 2
      },
      {
        name: 'hostName',
        validationRules: [
          {
            name: 'required',
            message: l10n('lcops.createEnvironment.mocks.required')
          },
          {
            name: 'hostname',
            message: l10n('lcops.createEnvironment.mocks.hostname')
          }
        ],
        displayRules: [],
        order: 3
      },
      {
        name: 'ip',
        validationRules: [
          {
            name: 'required',
            message: l10n('lcops.createEnvironment.mocks.required')
          },
          {
            name: 'ipAddress',
            message: l10n('lcops.createEnvironment.mocks.required')
          }
        ],
        displayRules: [],
        order: 4
      },
      {
        name: 'rootPassword',
        validationRules: [
          {
            name: 'required',
            message: l10n('lcops.createEnvironment.mocks.required')
          }
        ],
        displayRules: [],
        order: 5
      },
      {
        name: 'vCenterList',
        validationRules: [
          {
            name: 'anyChecked',
            message: l10n('lcops.createEnvironment.mocks.vCenterListChecked')
          }
        ],
        displayRules: [],
        validationFieldType: 'array',
        order: 6
      }
    ],
    abxcloudproxy: [
      {
        name: 'productPassword',
        validationRules: [
          {
            name: 'required',
            message: l10n('lcops.createEnvironment.mocks.required')
          }
        ],
        displayRules: [],
        order: 1
      },
      {
        name: 'proxyName',
        validationRules: [
          {
            name: 'required',
            message: l10n('lcops.createEnvironment.mocks.required')
          }
        ],
        displayRules: [],
        order: 0
      },
      {
        name: 'cloudProxyRefreshToken',
        validationRules: [
          {
            name: 'required',
            message: l10n('lcops.createEnvironment.mocks.required')
          }
        ],
        displayRules: [],
        order: 2
      },
      {
        name: 'hostName',
        validationRules: [
          {
            name: 'required',
            message: l10n('lcops.createEnvironment.mocks.required')
          },
          {
            name: 'hostname',
            message: l10n('lcops.createEnvironment.mocks.hostname')
          }
        ],
        displayRules: [],
        order: 3
      },
      {
        name: 'ip',
        validationRules: [
          {
            name: 'required',
            message: l10n('lcops.createEnvironment.mocks.required')
          },
          {
            name: 'ipAddress',
            message: l10n('lcops.createEnvironment.mocks.required')
          }
        ],
        displayRules: [],
        order: 4
      },
      {
        name: 'rootPassword',
        validationRules: [
          {
            name: 'required',
            message: l10n('lcops.createEnvironment.mocks.required')
          }
        ],
        displayRules: [],
        order: 5
      },
      {
        name: 'vCenterList',
        validationRules: [
          {
            name: 'anyChecked',
            message: l10n('lcops.createEnvironment.mocks.vCenterListChecked')
          }
        ],
        displayRules: [],
        validationFieldType: 'array',
        order: 6
      },
      {
        name: 'networkProxySettings',
        displayRules: [],
        validationRules: [],
        order: 100
      }
    ]
  };
};

export const nodeDisplayMetadata: {
  [key: string]: DisplayMetadata[];
} = {
  'generic-node': [
    {
      name: 'vmName',
      validationRules: [
        {
          name: 'required',
          message: l10n('lcops.createEnvironment.mocks.required')
        }
      ],
      displayRules: [],
      order: 0
    },
    {
      name: 'hostName',
      validationRules: [
        {
          name: 'required',
          message: l10n('lcops.createEnvironment.mocks.required')
        },
        {
          name: 'hostname',
          message: l10n('lcops.createEnvironment.mocks.hostname')
        }
      ],
      displayRules: [],
      order: 1
    },
    {
      name: 'ip',
      validationRules: [
        {
          name: 'required',
          message: l10n('lcops.createEnvironment.mocks.required')
        },
        {
          name: 'ipAddress',
          message: l10n('lcops.createEnvironment.mocks.required')
        }
      ],
      displayRules: [],
      order: 2
    },
    {
      name: 'ipv6',
      validationRules: [
        {
          name: 'required',
          message: l10n('lcops.createEnvironment.mocks.required')
        },
        {
          name: 'ipv6Address',
          message: l10n('lcops.createEnvironment.mocks.required')
        }
      ],
      displayRules: [],
      order: 3
    }
  ],
  'proxy-agent-vsphere': [
    {
      name: 'vmName',
      validationRules: [
        {
          name: 'required',
          message: l10n('lcops.createEnvironment.mocks.required')
        }
      ],
      displayRules: [],
      order: 0
    },
    {
      name: 'hostName',
      validationRules: [
        {
          name: 'required',
          message: l10n('lcops.createEnvironment.mocks.required')
        },
        {
          name: 'hostname',
          message: l10n('lcops.createEnvironment.mocks.hostname')
        }
      ],
      displayRules: [],
      order: 1
    },
    {
      name: 'ip',
      validationRules: [
        {
          name: 'required',
          message: l10n('lcops.createEnvironment.mocks.required')
        },
        {
          name: 'ipAddress',
          message: l10n('lcops.createEnvironment.mocks.required')
        }
      ],
      displayRules: [],
      order: 2
    },
    {
      name: 'vsphereEndpointName',
      validationRules: [],
      displayRules: [],
      order: 3
    }
  ],
  db: [
    {
      name: 'vmName',
      validationRules: [
        {
          name: 'required',
          message: l10n('lcops.createEnvironment.mocks.required')
        }
      ],
      displayRules: [],
      order: 0
    },
    {
      name: 'hostName',
      validationRules: [
        {
          name: 'required',
          message: l10n('lcops.createEnvironment.mocks.required')
        }
      ],
      displayRules: [],
      order: 1
    },
    {
      name: 'ip',
      validationRules: [
        {
          name: 'required',
          message: l10n('lcops.createEnvironment.mocks.required')
        },
        {
          name: 'ipAddress',
          message: l10n('lcops.createEnvironment.mocks.required')
        }
      ],
      displayRules: [],
      order: 2
    }
  ],
  'iaas-node': [
    {
      name: 'vmName',
      validationRules: [
        {
          name: 'required',
          message: l10n('lcops.createEnvironment.mocks.required')
        }
      ],
      displayRules: [],
      order: 0
    },
    {
      name: 'hostName',
      validationRules: [
        {
          name: 'required',
          message: l10n('lcops.createEnvironment.mocks.required')
        },
        {
          name: 'hostname',
          message: l10n('lcops.createEnvironment.mocks.hostname')
        },
        {
          name: 'pattern',
          message: l10n('lcops.createEnvironment.mocks.hostnamePattern'),
          value: /(?=^[^.]{1,15}\..*$)(?=^.{1,254}$)(^(?:(?!\d+\.)[a-zA-Z0-9_-]{1,63}\.)+(?:[a-zA-Z]{2,})$)/
        }
      ],
      displayRules: [],
      order: 1
    },
    {
      name: 'ip',
      validationRules: [
        {
          name: 'required',
          message: l10n('lcops.createEnvironment.mocks.required')
        },
        {
          name: 'ipAddress',
          message: l10n('lcops.createEnvironment.mocks.required')
        }
      ],
      displayRules: [],
      order: 2
    }
  ],
  'windows-box': [
    {
      name: 'windowsDeployType',
      validationRules: [],
      data: {
        options: [
          {
            textLabel: l10n('lcops.createEnvironment.mocks.iso'),
            value: 'iso'
          },
          {
            textLabel: l10n('lcops.createEnvironment.mocks.template'),
            value: 'template'
          }
        ]
      },
      displayRules: [],
      order: 0
    },
    {
      name: 'template',
      validationRules: [
        {
          name: 'required',
          message: l10n('lcops.createEnvironment.mocks.required')
        }
      ],
      url: {
        options:
          '/lcm/lcops/api/datacenters/{dataCenterVmid}/vCenter/{vCenterName}/query/vCDataCenters->{datacenter}::cluster->{clusterName}::templateCustomSpecs'
      },
      displayRules: [
        {
          fieldRef: 'windowsDeployType',
          fieldRules: [
            {
              name: 'template',
              value: 'template'
            }
          ]
        }
      ],
      order: 1
    },
    {
      name: 'isoFileName',
      validationRules: [
        {
          name: 'required',
          message: l10n('lcops.createEnvironment.mocks.isoFileNameRequired')
        }
      ],
      url: {
        options:
          '/lcm/lcops/api/settings/productbinarysettings?productId=WINDOWS'
      },
      displayRules: [
        {
          fieldRef: 'windowsDeployType',
          fieldRules: [
            {
              name: 'iso',
              value: 'iso'
            }
          ]
        }
      ],
      order: 2
    },
    {
      name: 'useCustomSpec',
      validationRules: [],
      displayRules: [],
      data: {
        options: [
          {
            textLabel: l10n('lcops.createEnvironment.mocks.existingSpec'),
            value: 'false'
          },
          {
            textLabel: l10n('lcops.createEnvironment.mocks.userInput'),
            value: 'true'
          }
        ]
      },
      order: 3
    },
    {
      name: 'orgName',
      validationRules: [
        {
          name: 'required',
          message: l10n('lcops.createEnvironment.mocks.orgNameRequired')
        }
      ],
      displayRules: [
        {
          fieldRef: 'useCustomSpec',
          fieldRules: [
            {
              name: 'isChecked',
              value: 'true'
            }
          ]
        }
      ],
      order: 4
    },
    {
      name: 'fullName',
      validationRules: [
        {
          name: 'required',
          message: l10n('lcops.createEnvironment.mocks.required')
        }
      ],
      displayRules: [
        {
          fieldRef: 'useCustomSpec',
          fieldRules: [
            {
              name: 'isChecked',
              value: 'true'
            }
          ]
        }
      ],
      order: 5
    },
    {
      name: 'windowsAdminPassword',
      validationRules: [
        {
          name: 'required',
          message: l10n('lcops.createEnvironment.mocks.required')
        }
      ],
      displayRules: [
        {
          fieldRef: 'useCustomSpec',
          fieldRules: [
            {
              name: 'isChecked',
              value: 'true'
            }
          ]
        }
      ],
      order: 6
    },
    {
      name: 'timezone',
      validationRules: [
        {
          name: 'required',
          message: l10n('lcops.createEnvironment.mocks.hostnameRequired')
        }
      ],
      data: {
        options: [
          {
            index: '000',
            timeZone: 'Dateline Standard Time (GMT-12:00)',
            displayName: l10n('lcops.createEnvironment.mocks.000')
          },
          {
            index: '001',
            timeZone: 'Samoa Standard Time (GMT-11:00)',
            displayName: l10n('lcops.createEnvironment.mocks.001')
          },
          {
            index: '002',
            timeZone: 'Hawaiian Standard Time (GMT-10:00)',
            displayName: l10n('lcops.createEnvironment.mocks.002')
          },
          {
            index: '003',
            timeZone: 'Alaskan Standard Time (GMT-09:00)',
            displayName: l10n('lcops.createEnvironment.mocks.003')
          },
          {
            index: '004',
            timeZone: 'Pacific Standard Time (GMT-08:00)',
            displayName: l10n('lcops.createEnvironment.mocks.004')
          },
          {
            index: '010',
            timeZone: 'Mountain Standard Time (GMT-07:00)',
            displayName: l10n('lcops.createEnvironment.mocks.010')
          },
          {
            index: '013',
            timeZone: 'Mexico Standard Time 2 (GMT-07:00)',
            displayName: l10n('lcops.createEnvironment.mocks.013')
          },
          {
            index: '015',
            timeZone: 'U.S. Mountain Standard Time (GMT-07:00)',
            displayName: l10n('lcops.createEnvironment.mocks.015')
          },
          {
            index: '020',
            timeZone: 'Central Standard Time (GMT-06:00)',
            displayName: l10n('lcops.createEnvironment.mocks.020')
          },
          {
            index: '025',
            timeZone: 'Canada Central Standard Time (GMT-06:00)',
            displayName: l10n('lcops.createEnvironment.mocks.025')
          },
          {
            index: '030',
            timeZone: 'Mexico Standard Time (GMT-06:00)',
            displayName: l10n('lcops.createEnvironment.mocks.030')
          },
          {
            index: '033',
            timeZone: 'Central America Standard Time (GMT-06:00)',
            displayName: l10n('lcops.createEnvironment.mocks.033')
          },
          {
            index: '035',
            timeZone: 'Eastern Standard Time (GMT-05:00)',
            displayName: l10n('lcops.createEnvironment.mocks.035')
          },
          {
            index: '040',
            timeZone: 'U.S. Eastern Standard Time (GMT-05:00)',
            displayName: l10n('lcops.createEnvironment.mocks.040')
          },
          {
            index: '045',
            timeZone: 'S.A. Pacific Standard Time (GMT-05:00)',
            displayName: l10n('lcops.createEnvironment.mocks.045')
          },
          {
            index: '050',
            timeZone: 'Atlantic Standard Time (GMT-04:00)',
            displayName: l10n('lcops.createEnvironment.mocks.050')
          },
          {
            index: '055',
            timeZone: 'S.A. Western Standard Time (GMT-04:00)',
            displayName: l10n('lcops.createEnvironment.mocks.055')
          },
          {
            index: '056',
            timeZone: 'Pacific S.A. Standard Time (GMT-04:00)',
            displayName: l10n('lcops.createEnvironment.mocks.056')
          },
          {
            index: '060',
            timeZone: 'Newfoundland Standard Time (GMT-03:30)',
            displayName: l10n('lcops.createEnvironment.mocks.060')
          },
          {
            index: '065',
            timeZone: 'E. South America Standard Time (GMT-03:00)',
            displayName: l10n('lcops.createEnvironment.mocks.065')
          },
          {
            index: '070',
            timeZone: 'S.A. Eastern Standard Time (GMT-03:00)',
            displayName: l10n('lcops.createEnvironment.mocks.070')
          },
          {
            index: '073',
            timeZone: 'Greenland Standard Time (GMT-03:00)',
            displayName: l10n('lcops.createEnvironment.mocks.073')
          },
          {
            index: '075',
            timeZone: 'Mid-Atlantic Standard Time (GMT-02:00)',
            displayName: l10n('lcops.createEnvironment.mocks.075')
          },
          {
            index: '080',
            timeZone: 'Azores Standard Time (GMT-01:00)',
            displayName: l10n('lcops.createEnvironment.mocks.080')
          },
          {
            index: '083',
            timeZone: 'Cape Verde Standard Time (GMT-01:00)',
            displayName: l10n('lcops.createEnvironment.mocks.083')
          },
          {
            index: '085',
            timeZone: 'GMT Standard Time (GMT)',
            displayName: l10n('lcops.createEnvironment.mocks.085')
          },
          {
            index: '090',
            timeZone: 'Greenwich Standard Time (GMT)',
            displayName: l10n('lcops.createEnvironment.mocks.090')
          },
          {
            index: '095',
            timeZone: 'Central Europe Standard Time (GMT+01:00)',
            displayName: l10n('lcops.createEnvironment.mocks.095')
          },
          {
            index: 100,
            timeZone: 'Central European Standard Time (GMT+01:00)',
            displayName: l10n('lcops.createEnvironment.mocks.100')
          },
          {
            index: 105,
            timeZone: 'Romance Standard Time (GMT+01:00)',
            displayName: l10n('lcops.createEnvironment.mocks.105')
          },
          {
            index: 110,
            timeZone: 'W. Europe Standard Time (GMT+01:00)',
            displayName: l10n('lcops.createEnvironment.mocks.110')
          },
          {
            index: 113,
            timeZone: 'W. Central Africa Standard Time (GMT+01:00)',
            displayName: l10n('lcops.createEnvironment.mocks.113')
          },
          {
            index: 115,
            timeZone: 'E. Europe Standard Time (GMT+02:00)',
            displayName: l10n('lcops.createEnvironment.mocks.115')
          },
          {
            index: 120,
            timeZone: 'Egypt Standard Time (GMT+02:00)',
            displayName: l10n('lcops.createEnvironment.mocks.120')
          },
          {
            index: 125,
            timeZone: 'FLE Standard Time (GMT+02:00)',
            displayName: l10n('lcops.createEnvironment.mocks.125')
          },
          {
            index: 130,
            timeZone: 'GTB Standard Time (GMT+02:00)',
            displayName: l10n('lcops.createEnvironment.mocks.130')
          },
          {
            index: 135,
            timeZone: 'Israel Standard Time (GMT+02:00)',
            displayName: l10n('lcops.createEnvironment.mocks.135')
          },
          {
            index: 140,
            timeZone: 'South Africa Standard Time (GMT+02:00)',
            displayName: l10n('lcops.createEnvironment.mocks.140')
          },
          {
            index: 145,
            timeZone: 'Russian Standard Time (GMT+03:00)',
            displayName: l10n('lcops.createEnvironment.mocks.145')
          },
          {
            index: 150,
            timeZone: 'Arab Standard Time (GMT+03:00)',
            displayName: l10n('lcops.createEnvironment.mocks.150')
          },
          {
            index: 155,
            timeZone: 'E. Africa Standard Time (GMT+03:00)',
            displayName: l10n('lcops.createEnvironment.mocks.155')
          },
          {
            index: 158,
            timeZone: 'Arabic Standard Time (GMT+03:00)',
            displayName: l10n('lcops.createEnvironment.mocks.158')
          },
          {
            index: 160,
            timeZone: 'Iran Standard Time (GMT+03:30)',
            displayName: l10n('lcops.createEnvironment.mocks.160')
          },
          {
            index: 165,
            timeZone: 'Arabian Standard Time (GMT+04:00)',
            displayName: l10n('lcops.createEnvironment.mocks.165')
          },
          {
            index: 170,
            timeZone: 'Caucasus Standard Time (GMT+04:00)',
            displayName: l10n('lcops.createEnvironment.mocks.170')
          },
          {
            index: 175,
            timeZone: 'Afghanistan Standard Time (GMT+04:30)',
            displayName: l10n('lcops.createEnvironment.mocks.175')
          },
          {
            index: 180,
            timeZone: 'Ekaterinburg Standard Time (GMT+05:00)',
            displayName: l10n('lcops.createEnvironment.mocks.180')
          },
          {
            index: 185,
            timeZone: 'West Asia Standard Time (GMT+05:00)',
            displayName: l10n('lcops.createEnvironment.mocks.185')
          },
          {
            index: 190,
            timeZone: 'India Standard Time (GMT+05:30)',
            displayName: l10n('lcops.createEnvironment.mocks.190')
          },
          {
            index: 193,
            timeZone: 'Nepal Standard Time (GMT+05:45)',
            displayName: l10n('lcops.createEnvironment.mocks.193')
          },
          {
            index: 195,
            timeZone: 'Central Asia Standard Time (GMT+06:00)',
            displayName: l10n('lcops.createEnvironment.mocks.195')
          },
          {
            index: 200,
            timeZone: 'Sri Lanka Standard Time (GMT+06:00)',
            displayName: l10n('lcops.createEnvironment.mocks.200')
          },
          {
            index: 201,
            timeZone: 'N. Central Asia Standard Time (GMT+06:00)',
            displayName: l10n('lcops.createEnvironment.mocks.201')
          },
          {
            index: 203,
            timeZone: 'Myanmar Standard Time (GMT+06:30)',
            displayName: l10n('lcops.createEnvironment.mocks.203')
          },
          {
            index: 205,
            timeZone: 'S.E. Asia Standard Time (GMT+07:00)',
            displayName: l10n('lcops.createEnvironment.mocks.205')
          },
          {
            index: 207,
            timeZone: 'North Asia Standard Time (GMT+07:00)',
            displayName: l10n('lcops.createEnvironment.mocks.207')
          },
          {
            index: 210,
            timeZone: 'China Standard Time (GMT+08:00)',
            displayName: l10n('lcops.createEnvironment.mocks.210')
          },
          {
            index: 215,
            timeZone: 'Singapore Standard Time (GMT+08:00)',
            displayName: l10n('lcops.createEnvironment.mocks.215')
          },
          {
            index: 220,
            timeZone: 'Taipei Standard Time (GMT+08:00)',
            displayName: l10n('lcops.createEnvironment.mocks.220')
          },
          {
            index: 225,
            timeZone: 'W. Australia Standard Time (GMT+08:00)',
            displayName: l10n('lcops.createEnvironment.mocks.225')
          },
          {
            index: 227,
            timeZone: 'North Asia East Standard Time (GMT+08:00)',
            displayName: l10n('lcops.createEnvironment.mocks.227')
          },
          {
            index: 230,
            timeZone: 'Korea Standard Time (GMT+09:00)',
            displayName: l10n('lcops.createEnvironment.mocks.230')
          },
          {
            index: 235,
            timeZone: 'Tokyo Standard Time (GMT+09:00)',
            displayName: l10n('lcops.createEnvironment.mocks.235')
          },
          {
            index: 240,
            timeZone: 'Yakutsk Standard Time (GMT+09:00)',
            displayName: l10n('lcops.createEnvironment.mocks.240')
          },
          {
            index: 245,
            timeZone: 'A.U.S. Central Standard Time (GMT+09:30)',
            displayName: l10n('lcops.createEnvironment.mocks.245')
          },
          {
            index: 250,
            timeZone: 'Cen. Australia Standard Time (GMT+09:30)',
            displayName: l10n('lcops.createEnvironment.mocks.250')
          },
          {
            index: 255,
            timeZone: 'A.U.S. Eastern Standard Time (GMT+10:00)',
            displayName: l10n('lcops.createEnvironment.mocks.255')
          },
          {
            index: 260,
            timeZone: 'E. Australia Standard Time (GMT+10:00)',
            displayName: l10n('lcops.createEnvironment.mocks.260')
          },
          {
            index: 265,
            timeZone: 'Tasmania Standard Time (GMT+10:00)',
            displayName: l10n('lcops.createEnvironment.mocks.265')
          },
          {
            index: 270,
            timeZone: 'Vladivostok Standard Time (GMT+10:00)',
            displayName: l10n('lcops.createEnvironment.mocks.270')
          },
          {
            index: 275,
            timeZone: 'West Pacific Standard Time (GMT+10:00)',
            displayName: l10n('lcops.createEnvironment.mocks.275')
          },
          {
            index: 280,
            timeZone: 'Central Pacific Standard Time (GMT+11:00)',
            displayName: l10n('lcops.createEnvironment.mocks.280')
          },
          {
            index: 285,
            timeZone: 'Fiji Islands Standard Time (GMT+12:00)',
            displayName: l10n('lcops.createEnvironment.mocks.285')
          },
          {
            index: 290,
            timeZone: 'New Zealand Standard Time (GMT+12:00)',
            displayName: l10n('lcops.createEnvironment.mocks.290')
          },
          {
            index: 300,
            timeZone: 'Tonga Standard Time (GMT+13:00)',
            displayName: l10n('lcops.createEnvironment.mocks.300')
          }
        ].map(i => ({
          textLabel: i.displayName,
          value: i.index
        }))
      },
      displayRules: [
        {
          fieldRef: 'useCustomSpec',
          fieldRules: [
            {
              name: 'isChecked',
              value: 'true'
            }
          ]
        }
      ],
      order: 7
    },
    {
      name: 'isAutologon',
      validationRules: [
        {
          name: 'required',
          message: l10n('lcops.createEnvironment.mocks.hostnameRequired')
        }
      ],
      data: {
        options: [
          {
            textLabel: l10n('lcops.createEnvironment.mocks.yes'),
            value: 'true'
          },
          {
            textLabel: l10n('lcops.createEnvironment.mocks.no'),
            value: 'false'
          }
        ]
      },
      displayRules: [
        {
          fieldRef: 'useCustomSpec',
          fieldRules: [
            {
              name: 'isChecked',
              value: 'true'
            }
          ]
        }
      ],
      order: 8
    },
    {
      name: 'autoLogonCount',
      validationRules: [
        {
          name: 'required',
          message: l10n('lcops.createEnvironment.mocks.hostnameRequired')
        }
      ],
      displayRules: [
        {
          fieldRef: 'isAutologon',
          fieldRules: [
            {
              name: 'isChecked',
              value: 'true'
            }
          ]
        }
      ],
      order: 9
    },
    {
      name: 'templateSpec',
      validationRules: [
        {
          name: 'required',
          message: l10n('lcops.createEnvironment.mocks.templateSpecRequired')
        }
      ],
      url: {
        options:
          '/lcm/lcops/api/datacenters/{dataCenterVmid}/vCenter/{vCenterName}/query/templateCustomSpecs'
      },
      displayRules: [
        {
          fieldRef: 'useCustomSpec',
          fieldRules: [
            {
              name: 'isChecked',
              value: 'false'
            }
          ]
        }
      ],
      order: 10
    }
  ],
  'vrni-platform': [
    {
      name: 'vmName',
      validationRules: [
        {
          name: 'required',
          message: l10n('lcops.createEnvironment.mocks.ipAddressRequired')
        }
      ],
      displayRules: [],
      order: 0
    },
    {
      name: 'ip',
      validationRules: [
        {
          name: 'required',
          message: l10n('lcops.createEnvironment.mocks.ipAddressRequired')
        },
        {
          name: 'ipAddress',
          message: l10n('lcops.createEnvironment.mocks.required')
        }
      ],
      displayRules: [],
      order: 1
    },
    {
      name: 'vrniNodeSize',
      validationRules: [
        {
          name: 'required',
          message: l10n('lcops.createEnvironment.mocks.nodeSizeRequired')
        }
      ],
      data: {
        options: [
          {
            textLabel: l10n('lcops.createEnvironment.mocks.medium'),
            value: 'medium'
          },
          {
            textLabel: l10n('lcops.createEnvironment.mocks.large'),
            value: 'large'
          },
          {
            textLabel: l10n('lcops.createEnvironment.mocks.xLarge'),
            value: 'extra_large'
          }
        ]
      },
      displayRules: [],
      order: 2
    }
  ],
  'vrni-collector': [
    {
      name: 'vmName',
      validationRules: [
        {
          name: 'required',
          message: l10n('lcops.createEnvironment.mocks.required')
        }
      ],
      displayRules: [],
      order: 0
    },
    {
      name: 'ip',
      validationRules: [
        {
          name: 'required',
          message: l10n('lcops.createEnvironment.mocks.ipAddressRequired')
        },
        {
          name: 'ipAddress',
          message: l10n('lcops.createEnvironment.mocks.required')
        }
      ],
      displayRules: [],
      order: 1
    },
    {
      name: 'vrniNodeSize',
      validationRules: [
        {
          name: 'required',
          message: l10n('lcops.createEnvironment.mocks.nodeSizeRequired')
        }
      ],
      data: {
        options: [
          {
            textLabel: l10n('lcops.createEnvironment.mocks.medium'),
            value: 'medium'
          },
          {
            textLabel: l10n('lcops.createEnvironment.mocks.large'),
            value: 'large'
          },
          {
            textLabel: l10n('lcops.createEnvironment.mocks.xLarge'),
            value: 'extra_large'
          },
          {
            textLabel: l10n('lcops.createEnvironment.mocks.xxLarge'),
            value: 'double_extra_large'
          }
        ]
      },
      displayRules: [],
      order: 2
    }
  ],
  'vidm-connector': [
    {
      name: 'vmName',
      validationRules: [
        {
          name: 'required',
          message: l10n('lcops.createEnvironment.mocks.required')
        }
      ],
      displayRules: [],
      order: 0
    },
    {
      name: 'hostName',
      validationRules: [
        {
          name: 'required',
          message: l10n('lcops.createEnvironment.mocks.hostnameRequired')
        },
        {
          name: 'hostname',
          message: l10n('lcops.createEnvironment.mocks.hostname')
        }
      ],
      displayRules: [],
      order: 1
    },
    {
      name: 'vidmConnectorName',
      validationRules: [
        {
          name: 'required',
          message: l10n('lcops.createEnvironment.mocks.required')
        }
      ],
      displayRules: [],
      order: 2
    },
    {
      name: 'vidmConnectorAdminPassword',
      validationRules: [
        {
          name: 'required',
          message: l10n('lcops.createEnvironment.mocks.required')
        }
      ],
      displayRules: [],
      order: 3
    },
    {
      name: 'domainAdmin',
      validationRules: [
        {
          name: 'required',
          message: l10n('lcops.createEnvironment.mocks.required')
        },
        {
          name: 'pattern',
          message: l10n('lcops.createEnvironment.mocks.domainNamePattern'),
          value: WINDOWS_DOMAIN_NAME_REGEX
        }
      ],
      displayRules: [],
      order: 5
    },
    {
      name: 'domainAdminPassword',
      validationRules: [
        {
          name: 'required',
          message: l10n('lcops.createEnvironment.mocks.required')
        }
      ],
      displayRules: [],
      order: 6
    }
  ],
  retentionThreasold: [
    {
      name: 'vrliDataInterval',
      validationRules: [
        {
          name: 'required',
          message: l10n('lcops.createEnvironment.mocks.internalValueRequired')
        },
        {
          name: 'pattern',
          message: l10n('lcops.createEnvironment.mocks.internalValuePattern'),
          value: /^[1-9][0-9]*$/
        }
      ],
      displayRules: [],
      order: 0
    },
    {
      name: 'vrliIntervalUnit',
      validationRules: [
        {
          name: 'required',
          message: l10n('lcops.createEnvironment.mocks.internalUnitRequired')
        }
      ],
      data: {
        options: [
          {
            textLabel: l10n('lcops.createEnvironment.mocks.minutes'),
            value: 'MINUTES'
          },
          {
            textLabel: l10n('lcops.createEnvironment.mocks.hours'),
            value: 'HOURS'
          },
          {
            textLabel: l10n('lcops.createEnvironment.mocks.days'),
            value: 'DAYS'
          },
          {
            textLabel: l10n('lcops.createEnvironment.mocks.weeks'),
            value: 'WEEKS'
          },
          {
            textLabel: l10n('lcops.createEnvironment.mocks.months'),
            value: 'MONTHS'
          }
        ]
      },
      displayRules: [],
      order: 1
    }
  ],
  smtpConfiguration: [
    {
      name: 'vrliSMTPDefaultSenderEmail',
      validationRules: [
        {
          name: 'required',
          message: l10n('lcops.createEnvironment.mocks.senderEmailRequired')
        }
      ],
      order: 5,
      displayRules: []
    },
    {
      name: 'vrliSMTPIsSSLAuth',
      validationRules: [
        {
          name: 'required',
          message: l10n('lcops.createEnvironment.mocks.sslRequired')
        }
      ],
      order: 3,
      displayRules: []
    },
    {
      name: 'vrliSMTPIsTLS',
      validationRules: [
        {
          name: 'required',
          message: l10n('lcops.createEnvironment.mocks.tlsRequired')
        }
      ],
      order: 4,
      displayRules: []
    },
    {
      name: 'vrliSMTPLogin',
      validationRules: [],
      order: 6,
      displayRules: []
    },
    {
      name: 'vrliSMTPPassword',
      validationRules: [],
      order: 7,
      displayRules: []
    },
    {
      name: 'vrliSMTPPort',
      validationRules: [
        {
          name: 'required',
          message: l10n('lcops.createEnvironment.mocks.smtpPortRequired')
        },
        {
          name: 'pattern',
          message: l10n('lcops.createEnvironment.mocks.smtpPortPattern'),
          value: /^[0-9]+$/
        }
      ],
      order: 1,
      displayRules: []
    },
    {
      name: 'vrliSMTPServer',
      validationRules: [
        {
          name: 'required',
          message: l10n('lcops.createEnvironment.mocks.smtpServerRequired')
        }
      ],
      order: 0,
      displayRules: []
    }
  ],
  configureActiveDirectory: [
    {
      name: 'vrliADDomain',
      validationRules: [
        {
          name: 'required',
          message: l10n('lcops.createEnvironment.mocks.vrliADDomainRequired')
        }
      ],
      displayRules: [],
      order: 0
    },
    {
      name: 'vrliADDomainControllers',
      validationRules: [],
      displayRules: [],
      order: 1
    },
    {
      name: 'vrliADUsername',
      validationRules: [
        {
          name: 'required',
          message: l10n('lcops.createEnvironment.mocks.vrliADUsernameRequired')
        }
      ],
      displayRules: [],
      order: 2
    },
    {
      name: 'vrliADPassword',
      validationRules: [
        {
          name: 'required',
          message: l10n('lcops.createEnvironment.mocks.vrliADPasswordRequired')
        }
      ],
      displayRules: [],
      order: 3
    },
    {
      name: 'vrliADConnectionType',
      validationRules: [
        {
          name: 'required',
          message: l10n(
            'lcops.createEnvironment.mocks.vrliADConnectionTypeRequired'
          )
        }
      ],
      data: {
        options: [
          {
            textLabel: l10n('lcops.createEnvironment.mocks.standard'),
            value: 'STANDARD'
          },
          {
            textLabel: l10n('lcops.createEnvironment.mocks.globalCatalog'),
            value: 'GLOBAL_CAT'
          },
          {
            textLabel: l10n('lcops.createEnvironment.mocks.custom'),
            value: 'CUSTOM'
          }
        ]
      },
      displayRules: [],
      order: 4
    },
    {
      name: 'vrliIsSSLOnly',
      validationRules: [
        {
          name: 'required',
          message: l10n('lcops.createEnvironment.mocks.vrliIsSSLOnlyRequired')
        }
      ],
      displayRules: [],
      order: 5
    },
    {
      name: 'vrliADPort',
      validationRules: [
        {
          name: 'required',
          message: l10n('lcops.createEnvironment.mocks.vrliADPortRequired')
        },
        {
          name: 'pattern',
          message: l10n('lcops.createEnvironment.mocks.vrliADPortPattern'),
          value: /^[0-9]+$/
        }
      ],
      displayRules: [
        {
          fieldRef: 'vrliADConnectionType',
          fieldRules: [
            {
              name: 'Custom',
              value: 'CUSTOM'
            }
          ]
        }
      ],
      order: 6
    }
  ],
  logInsightForwarding: [],
  vrliAdditionalDisk: [
    {
      name: 'extendHardDiskCapacity',
      validationRules: [
        {
          name: 'required',
          message: l10n('lcops.createEnvironment.mocks.hdCapacityRequired')
        },
        {
          name: 'pattern',
          message: l10n('lcops.createEnvironment.mocks.hdCapacityPattern'),
          value: /^[1-9][0-9]*$/
        }
      ],
      displayRules: [],
      order: 0
    },
    {
      name: 'extendHardDiskFormat',
      validationRules: [
        {
          name: 'required',
          message: l10n('lcops.createEnvironment.mocks.hdFormatRequired')
        }
      ],
      data: {
        options: [
          {
            textLabel: l10n('lcops.createEnvironment.mocks.thickLazyZeroed'),
            value: 'THICK_LAZY_ZEROED'
          },
          {
            textLabel: l10n('lcops.createEnvironment.mocks.thickEagerZeroed'),
            value: 'THICK_EAGER_ZEROED'
          },
          {
            textLabel: l10n('lcops.createEnvironment.mocks.thin'),
            value: 'THIN'
          }
        ]
      },
      displayRules: [],
      order: 1
    }
  ],
  cloudproxy: [
    {
      name: 'vmName',
      validationRules: [
        {
          name: 'required',
          message: l10n('lcops.createEnvironment.mocks.required')
        }
      ],
      displayRules: [],
      order: 0
    },
    {
      name: 'hostName',
      validationRules: [
        {
          name: 'required',
          message: l10n('lcops.createEnvironment.mocks.required')
        },
        {
          name: 'hostname',
          message: l10n('lcops.createEnvironment.mocks.hostname')
        }
      ],
      displayRules: [],
      order: 1
    },
    {
      name: 'ip',
      validationRules: [
        {
          name: 'required',
          message: l10n('lcops.createEnvironment.mocks.required')
        },
        {
          name: 'ipAddress',
          message: l10n('lcops.createEnvironment.mocks.required')
        }
      ],
      displayRules: [],
      order: 2
    },
    {
      name: 'ipv6',
      validationRules: [
        {
          name: 'required',
          message: l10n('lcops.createEnvironment.mocks.required')
        },
        {
          name: 'ipv6Address',
          message: l10n('lcops.createEnvironment.mocks.required')
        }
      ],
      displayRules: [],
      order: 3
    },
    {
      name: 'deployOption',
      validationRules: [
        {
          name: 'required',
          message: l10n('lcops.createEnvironment.mocks.required')
        }
      ],
      data: {
        options: [
          {
            textLabel: l10n('lcops.createEnvironment.mocks.small'),
            value: 'smallcp'
          },
          {
            textLabel: l10n('lcops.createEnvironment.mocks.standard'),
            value: 'standardcp'
          }
        ]
      },
      displayRules: [],
      order: 4
    }
  ],
  remotecollector: [
    {
      name: 'vmName',
      validationRules: [
        {
          name: 'required',
          message: l10n('lcops.createEnvironment.mocks.required')
        }
      ],
      displayRules: [],
      order: 0
    },
    {
      name: 'hostName',
      validationRules: [
        {
          name: 'required',
          message: l10n('lcops.createEnvironment.mocks.required')
        },
        {
          name: 'hostname',
          message: l10n('lcops.createEnvironment.mocks.hostname')
        }
      ],
      displayRules: [],
      order: 1
    },
    {
      name: 'ip',
      validationRules: [
        {
          name: 'required',
          message: l10n('lcops.createEnvironment.mocks.required')
        },
        {
          name: 'ipAddress',
          message: l10n('lcops.createEnvironment.mocks.required')
        }
      ],
      displayRules: [],
      order: 2
    },
    {
      name: 'deployOption',
      validationRules: [
        {
          name: 'required',
          message: l10n('lcops.createEnvironment.mocks.required')
        }
      ],
      data: {
        options: [
          {
            textLabel: l10n('lcops.createEnvironment.mocks.standard'),
            value: 'smallrc'
          },
          {
            textLabel: l10n('lcops.createEnvironment.mocks.large'),
            value: 'largerc'
          }
        ]
      },
      displayRules: [],
      order: 3
    }
  ],
  ...vrniDSNodesDisplayMetadata
};
